
import { type Crop as CropType } from 'react-image-crop';
import ReactCrop from 'react-image-crop';
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PhotoCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previewUrl: string | null;
  crop: CropType | undefined;
  onCropChange: (crop: CropType) => void;
  onSave: () => void;
  onCancel: () => void;
  uploading: boolean;
  imgRef: React.RefObject<HTMLImageElement>;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

export const PhotoCropDialog = ({
  open,
  onOpenChange,
  previewUrl,
  crop,
  onCropChange,
  onSave,
  onCancel,
  uploading,
  imgRef,
  onImageLoad,
}: PhotoCropDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adjust Profile Picture</DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            Drag to position your photo and use the corners to adjust the crop area
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Your photo will be cropped to a circle for your profile</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {previewUrl && (
            <div className="max-h-[60vh] overflow-hidden">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => onCropChange(percentCrop)}
                circularCrop
                aspect={1}
                className="max-w-full mx-auto"
              >
                <img
                  ref={imgRef}
                  src={previewUrl}
                  alt="Preview"
                  onLoad={onImageLoad}
                  className="max-w-full max-h-[60vh] mx-auto"
                />
              </ReactCrop>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
